/**
 * The month of deadlines.
 *
 * Five tables already hold dates - tasks, milestones, projects, invoices and
 * planned next steps - and until now there was nowhere to see them together.
 * That is the whole ambition of this screen: it is a VIEW, not a planner. There
 * is no dragging, no creating, no Google or Outlook. Ring 1 made
 * `Activity.dueAt` writable and this is the first place where a month's worth
 * of "call them back" is visible at all.
 *
 * The one thing it CREATES is a planned next step, and only that one. Four of
 * the five sources are records with a life of their own - an invoice has a
 * client, lines and money on it - and a calendar that opened an invoice form
 * would be a second, worse sales screen. The fifth is a note about a company
 * with a date on it, which is the only kind of thing a month view is the right
 * place to add. See `PlanStep.tsx`.
 *
 * Two things it still refuses to do:
 *
 *  - **Colour the sources the same.** An invoice due is money expected, a task
 *    due is be-finished, a project deadline is handed-over. One colour would
 *    turn five different promises into one pile.
 *  - **Hide what is finished.** Done items stay, drawn quietly. A month with the
 *    finished work removed reads as a month where nothing happened.
 *
 * Narrow screens get a day list instead of a grid. Seven columns on a phone is
 * seven columns of nothing legible, and the answer to "what is due" reads
 * better as a list anyway.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CalendarPlus, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react";
import { cn } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { getCalendar, type CalendarItem, type CalendarPayload } from "../../../lib/calendar";
import { Btn, EmptyState, PageHead, useViewportSize } from "../../ui";
import { ContextMenu, useContextMenu, type ContextMenuHandle } from "../../shell/ContextMenu";
import { DayMenuItems } from "./DayMenu";
import { MonthPicker } from "./MonthPicker";
import { PlanStepDialog } from "./PlanStep";
import { KIND_DOT, KIND_ICON, itemTitle, type T } from "./kinds";
import {
  dayKey, gridWindow, groupByDay, monthGrid, monthLabel, shiftMonth, timeLabel, weekdayLabels,
  type DayCell,
} from "./lib";

/** How many items a day cell shows before it says "+3 more". Four fits the cell
 *  at every width the grid is drawn at; the rest are one click away in the day
 *  list below. */
const PER_CELL = 4;

/** Below this the grid becomes a day list. Seven columns need the room. */
const GRID_MIN_WIDTH = 860;

/**
 * Below this the GRID drops the clock off its chips. The day list never does.
 *
 * Measured, not guessed. At 880px - the narrowest the grid is ever drawn - a
 * cell is about 118px and a chip 106px. A clock takes roughly 35 of those, and
 * what is left for the title is 23px: "Vaata kujundus ule" renders as "Va...".
 * A chip whose time you can read and whose subject you cannot is worse than the
 * chip that was there before, so under this width the time stays in the
 * tooltip, where it costs nothing.
 *
 * The number is where a title gets about 85px back, allowing for the sidebar
 * rail the harness does not draw. The day list is exempt because its rows are
 * the full width of the card - the narrow view has the roomier rows, which is
 * the opposite of what it sounds like.
 */
const TIME_MIN_WIDTH = 1180;

export function CalendarSection() {
  const t = useT();
  const { locale } = useI18n();
  const nav = useNavigate();
  const { vw } = useViewportSize();

  const today = useMemo(() => new Date(), []);
  const [at, setAt] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() }));
  const [payload, setPayload] = useState<CalendarPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Bumped after a step is planned. The window is the only other thing the fetch
  // depends on, and planning does not move it, so without a counter the new
  // entry would not appear until the operator changed month and came back.
  const [reloads, setReloads] = useState(0);
  /** The day the plan dialog is open on, or null when it is closed. */
  const [planning, setPlanning] = useState<Date | null>(null);
  /**
   * One menu for the whole month, not one per cell.
   *
   * Forty-two cells each mounting a portal and four window listeners is a page
   * that stutters on every month change; the cell travels in the handle's state
   * instead. See `shell/ContextMenu.tsx`, which the list desks already share.
   */
  const menu = useContextMenu<DayCell>();

  const cells = useMemo(() => monthGrid(at.year, at.month, today), [at, today]);
  const window = useMemo(() => gridWindow(cells), [cells]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setFailed(false);
    getCalendar(window.from, window.to, ctrl.signal)
      .then((res) => {
        setPayload(res);
        setLoading(false);
      })
      .catch(() => {
        if (ctrl.signal.aborted) return;
        setFailed(true);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [window.from, window.to, reloads]);

  const byDay = useMemo(() => groupByDay(payload?.items ?? []), [payload]);
  const open = useCallback((item: CalendarItem) => { if (item.to) nav(item.to); }, [nav]);
  const plan = useCallback((day: Date) => setPlanning(day), []);

  const step = (delta: number) => setAt((prev) => shiftMonth(prev.year, prev.month, delta));
  const total = payload?.items.length ?? 0;
  const compact = vw < GRID_MIN_WIDTH;

  /**
   * The day the head's own Plan button starts on.
   *
   * Today while today is on screen, and the first of the month otherwise. The
   * dialog's date field is fully editable either way - this is a starting point,
   * not a constraint - but a button that always meant "today" would silently
   * plan into August while the operator was reading October.
   */
  const planFromHead = () =>
    plan(
      at.year === today.getFullYear() && at.month === today.getMonth()
        ? today
        : new Date(at.year, at.month, 1),
    );

  return (
    <>
      <PageHead
        // The heading is the month picker. The arrows beside it step one month,
        // which is the right control for "next month" and a bad one for "next
        // June" - and there was no control at all for another YEAR. Putting it
        // in the title rather than the actions strip is deliberate: that strip
        // is shrinkable, so anything added to it comes out of this title's
        // width first.
        title={
          <MonthPicker
            year={at.year}
            month={at.month}
            locale={locale}
            label={monthLabel(at.year, at.month, locale)}
            onPick={(year, month) => setAt({ year, month })}
          />
        }
        sub={payload ? t("calendar.sub", { n: total }) : t("common.loading")}
        actions={
          <>
            <Btn onClick={() => step(-1)} aria-label={t("calendar.prev")}>
              <ChevronLeft className="size-4" />
            </Btn>
            <Btn onClick={() => setAt({ year: today.getFullYear(), month: today.getMonth() })}>
              {t("calendar.today")}
            </Btn>
            <Btn onClick={() => step(1)} aria-label={t("calendar.next")}>
              <ChevronRight className="size-4" />
            </Btn>
            {/* The head button is what makes an EMPTY day reachable on a narrow
                screen: the day list only draws days that already have something
                on them, so the per-cell control cannot be the only way in. */}
            <Btn tone="brand" onClick={planFromHead}>
              <CalendarPlus className="size-4" />
              {t("calendar.plan")}
            </Btn>
          </>
        }
      />

      {/* The legend is built from what the server was ALLOWED to read, not from
          the five kinds that exist. Somebody without the sales key sees no
          invoice line, rather than an invoice line that is always empty. */}
      {payload ? (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          {payload.sources.map((kind) => (
            <span key={kind} className="flex items-center gap-1.5">
              <span className={cn("size-2 rounded-full", KIND_DOT[kind])} />
              {t(`calendar.kind.${kind}`)}
              <span className="tabular-nums opacity-70">{payload.counts[kind]}</span>
            </span>
          ))}
        </div>
      ) : null}

      <div className={cn("crm-card", compact ? "" : "p-0")}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : failed ? (
          <EmptyState icon={CalendarDays} title={t("common.error")} />
        ) : compact ? (
          // The day list draws full-width rows, so it can always afford the clock.
          <DayList cells={cells} byDay={byDay} onOpen={open} onPlan={plan} menu={menu} showTime t={t} locale={locale} />
        ) : (
          <MonthGrid cells={cells} byDay={byDay} onOpen={open} onPlan={plan} menu={menu} showTime={vw >= TIME_MIN_WIDTH} t={t} locale={locale} />
        )}
      </div>

      {payload?.truncated ? (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{t("calendar.truncated")}</p>
      ) : null}

      {/* Everything the day holds, including the entries a cell hid behind
          "+3 more" - which on the wide grid had no other way in at all. */}
      <ContextMenu menu={menu} label={t("calendar.dayMenu")}>
        {(cell, close) => (
          <DayMenuItems
            day={cell.date}
            items={byDay.get(cell.key) ?? []}
            onOpen={open}
            onPlan={plan}
            close={close}
            t={t}
            locale={locale}
          />
        )}
      </ContextMenu>

      {planning ? (
        <PlanStepDialog
          day={planning}
          onClose={() => setPlanning(null)}
          onPlanned={() => setReloads((n) => n + 1)}
        />
      ) : null}
    </>
  );
}

interface ViewProps {
  cells: ReturnType<typeof monthGrid>;
  byDay: Map<string, CalendarItem[]>;
  onOpen: (item: CalendarItem) => void;
  onPlan: (day: Date) => void;
  /** The day menu, raised by a right-click anywhere on the day and by a plain
   *  click on its date. */
  menu: ContextMenuHandle<DayCell>;
  /** Whether the chips have the room to print a clock. See `TIME_MIN_WIDTH`. */
  showTime: boolean;
  t: T;
  locale: string;
}

/** One item, wherever it is drawn. A done item keeps its place and loses its
 *  weight - struck through, dimmed, same colour. */
function ItemChip({
  item, onOpen, showTime, t, locale,
}: { item: CalendarItem; onOpen: (i: CalendarItem) => void; showTime: boolean; t: T; locale: string }) {
  const Icon = KIND_ICON[item.kind];
  const title = itemTitle(item, t);
  // Null on the three sources that mean a DAY and store midnight. See
  // `timeLabel` - a clock on every invoice would be noise, and "00:00" would be
  // a deadline nobody set.
  const at = timeLabel(new Date(item.at), locale);
  const time = showTime ? at : null;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      disabled={!item.to}
      // The tooltip always carries the clock, including at the widths where the
      // chip cannot afford to print it.
      title={`${t(`calendar.kind.${item.kind}`)}: ${at ? `${at} ` : ""}${title}${item.sub ? ` - ${item.sub}` : ""}`}
      className={cn(
        "flex w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-xs",
        item.to ? "hover:bg-muted" : "cursor-default",
        item.done && "opacity-55",
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", KIND_DOT[item.kind])} />
      {/* The icon gives way to the clock rather than sitting beside it. Both
          encode the same thing - which source this is - and the dot to the left
          already says it, with the legend above as the key. Keeping all three
          costs 18px of title on a chip that has about 100. */}
      {time ? null : <Icon className="size-3 shrink-0 text-muted-foreground" />}
      {/* Ahead of the title, and never allowed to shrink: the title truncates
          and the clock does not, because a half-read title still says which
          thing it is and a half-read time says the wrong hour. */}
      {time ? <span className="shrink-0 tabular-nums text-muted-foreground">{time}</span> : null}
      <span className={cn("min-w-0 flex-1 truncate", item.done && "line-through")}>{title}</span>
    </button>
  );
}

/**
 * The "plan something here" control on a day.
 *
 * Quiet rather than hidden, and that is the whole decision. The obvious build is
 * `opacity-0` plus `group-hover`, which reads well on a laptop and leaves the
 * control unreachable on every touch screen - there is no hover to give, so the
 * plus never appears and the day cannot be planned on at all. A tablet in
 * landscape is past `GRID_MIN_WIDTH`, so this is not a phone-only corner: it is
 * the grid itself, on a device with a finger.
 *
 * So it is always drawn, at a third of its weight, and comes up to full on hover
 * and on keyboard focus. Forty-two faint plus signs cost less than one control
 * that is missing on the hardware nobody tested on.
 */
function PlanButton({ day, onPlan, t }: { day: Date; onPlan: (d: Date) => void; t: T }) {
  return (
    <button
      type="button"
      onClick={() => onPlan(day)}
      title={t("calendar.plan")}
      aria-label={t("calendar.plan")}
      className={
        "flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground " +
        "opacity-30 transition-opacity hover:bg-muted hover:text-foreground " +
        "hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
      }
    >
      <Plus className="size-3.5" />
    </button>
  );
}

/**
 * The date in its circle, which is now the way into the day menu.
 *
 * It was a `<span>` and it did nothing. The cell already had two live controls
 * (the plus, and the chips) and no room for a third, so rather than invent one
 * the number itself became it: the operator's first instinct on a calendar is
 * to click the date, and until now that instinct hit dead pixels.
 *
 * `menu.open` takes the pointer position off the event, so a left click places
 * the panel exactly where a right click would - one menu, one placement rule,
 * two gestures.
 */
function DayNumber({ cell, menu, t }: { cell: DayCell; menu: ContextMenuHandle<DayCell>; t: T }) {
  return (
    <button
      type="button"
      onClick={(e) => menu.open(e, cell)}
      aria-haspopup="menu"
      title={t("calendar.dayMenu")}
      className={cn(
        "flex size-6 items-center justify-center rounded-full text-xs tabular-nums transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        cell.isToday
          ? "bg-primary font-bold text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        !cell.inMonth && !cell.isToday && "opacity-50",
      )}
    >
      {cell.date.getDate()}
    </button>
  );
}

/** The wide view: whole weeks, Monday first. */
function MonthGrid({ cells, byDay, onOpen, onPlan, menu, showTime, t, locale }: ViewProps) {
  const heads = useMemo(() => weekdayLabels(locale), [locale]);
  return (
    <div>
      <div className="grid grid-cols-7 border-b border-border">
        {heads.map((label) => (
          <div key={label} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          const items = byDay.get(cell.key) ?? [];
          const shown = items.slice(0, PER_CELL);
          const rest = items.length - shown.length;
          // The card draws the outer frame, so the last column and the last row
          // drop theirs. Two borders on one line is a hairline that looks like a
          // rendering fault rather than a design.
          const lastCol = (i + 1) % 7 === 0;
          const lastRow = i >= cells.length - 7;
          return (
            <div
              key={cell.key}
              {...menu.rowProps(cell)}
              className={cn(
                // `group` is what reveals the plan button on hover. Without it
                // the button's own `group-hover` matches nothing and it stays
                // invisible to the mouse.
                "group min-h-[112px] border-border p-1.5",
                !lastCol && "border-r",
                !lastRow && "border-b",
                !cell.inMonth && "bg-muted/30",
              )}
            >
              {/* `justify-between`, not `justify-end` with a spacer: the date
                  keeps its right edge and the plus takes the room on the left
                  that was empty anyway. */}
              <div className="mb-1 flex items-center justify-between">
                <PlanButton day={cell.date} onPlan={onPlan} t={t} />
                <DayNumber cell={cell} menu={menu} t={t} />
              </div>
              <div className="space-y-0.5">
                {shown.map((item) => (
                  <ItemChip key={item.key} item={item} onOpen={onOpen} showTime={showTime} t={t} locale={locale} />
                ))}
                {rest > 0 ? (
                  <span className="block px-1.5 text-[11px] text-muted-foreground">
                    {t("calendar.more", { n: rest })}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The narrow view: only the days that have something, in order.
 *
 * Which is why the plan control here is a per-day convenience and not the way
 * in: an empty Thursday draws no row to hang a plus off. The head button covers
 * that case, and its dialog can be moved to any date.
 */
function DayList({ cells, byDay, onOpen, onPlan, menu, showTime, t, locale }: ViewProps) {
  const days = cells.filter((c) => (byDay.get(c.key)?.length ?? 0) > 0);
  if (!days.length) {
    return <EmptyState icon={CalendarDays} title={t("calendar.empty")} body={t("calendar.emptyBody")} />;
  }
  const fmt = (at: Date) => {
    try {
      return new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(at);
    } catch {
      return dayKey(at);
    }
  };
  return (
    <ul className="divide-y divide-border">
      {days.map((cell) => (
        <li key={cell.key} {...menu.rowProps(cell)} className="group py-2 first:pt-0 last:pb-0">
          <div className="mb-1 flex items-center justify-between gap-2">
            {/* The heading is the menu's button here, the way the date circle is
                on the grid: same gesture, whichever view the width chose. */}
            <button
              type="button"
              onClick={(e) => menu.open(e, cell)}
              aria-haspopup="menu"
              title={t("calendar.dayMenu")}
              className={cn(
                "-mx-1 min-w-0 truncate rounded-md px-1 text-left text-xs font-semibold transition-colors",
                "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                cell.isToday ? "text-primary" : "text-muted-foreground",
              )}
            >
              {fmt(cell.date)}
            </button>
            <PlanButton day={cell.date} onPlan={onPlan} t={t} />
          </div>
          <div className="space-y-0.5">
            {(byDay.get(cell.key) ?? []).map((item) => (
              <ItemChip key={item.key} item={item} onOpen={onOpen} showTime={showTime} t={t} locale={locale} />
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}
